import { buildAuthUrl, createPkcePair, randomState, type OAuthConfig } from "@app/storage";
import { detectPlatform } from "../../platform.js";

export class GoogleOAuthError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "GoogleOAuthError";
  }
}

export interface OAuthResult {
  code: string;
  verifier: string;
  redirectUri: string;
}

/** Per-request OAuth options (distinct from the static client {@link OAuthConfig}). */
export interface OAuthFlowOptions {
  /** Pre-fill the account chooser with a known email. */
  loginHint?: string;
  /** Request a refresh token. Default true (Calendar). Identity sign-in: false. */
  offline?: boolean;
  /** Override Google's consent prompt. Default "consent". */
  prompt?: string;
}

/**
 * Run the Authorization Code + PKCE flow and return the code + verifier for the
 * caller to exchange. Platform-branched (mirrors `file-io.ts`):
 *  - browser: a popup to {@link redirectPath} that `postMessage`s the code back.
 *  - capacitor (Android): the system browser (Custom Tabs) + a custom-scheme
 *    redirect captured via `@capacitor/app`'s `appUrlOpen`.
 *  - tauri: native loopback capture (follow-up — still throws).
 *
 * The PKCE verifier never leaves the device and there is no client secret.
 */
export async function runOAuthFlow(cfg: OAuthConfig, opts: OAuthFlowOptions = {}): Promise<OAuthResult> {
  const platform = detectPlatform();
  if (platform === "capacitor") return runCapacitorFlow(cfg, opts);
  if (platform !== "browser") {
    throw new GoogleOAuthError(
      "Connecting Google Calendar isn’t supported on the desktop app yet — use the web app.",
    );
  }
  const { verifier, challenge } = await createPkcePair();
  const state = randomState();
  const redirectUri = `${window.location.origin}${redirectPath}`;
  const url = buildAuthUrl(cfg, { challenge, state, redirectUri, ...opts });
  const code = await openPopupAndAwait(url, state);
  return { code, verifier, redirectUri };
}

/** The SPA route Google redirects the popup to (must be a registered redirect
 *  URI in the Google Cloud OAuth client). Rendered by `routes/oauth-callback.tsx`. */
export const redirectPath = "/oauth-callback";

/** The native custom-scheme redirect for a Google client id. Google's installed-
 *  app convention is the REVERSED client id (`NNNN.apps.googleusercontent.com` →
 *  `com.googleusercontent.apps.NNNN`) as the scheme; the Android manifest's
 *  `googleAuthScheme` placeholder must equal that scheme. */
export function nativeRedirectUri(clientId: string): string {
  return `${nativeRedirectScheme(clientId)}:/oauth2redirect`;
}

function nativeRedirectScheme(clientId: string): string {
  const m = /^(.+)\.apps\.googleusercontent\.com$/.exec(clientId);
  return m ? `com.googleusercontent.apps.${m[1]}` : clientId;
}

// --- browser popup ---

interface CallbackMessage {
  source?: string;
  code?: string;
  state?: string;
  error?: string;
}

function openPopupAndAwait(url: string, expectedState: string): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const popup = window.open(url, "google-oauth", "width=500,height=660,menubar=no,toolbar=no");
    if (!popup) {
      reject(new GoogleOAuthError("Pop-up blocked. Allow pop-ups for this site and try again."));
      return;
    }
    let settled = false;
    const finish = (fn: () => void) => {
      if (settled) return;
      settled = true;
      clearInterval(timer);
      window.removeEventListener("message", onMessage);
      try { popup.close(); } catch { /* already closed */ }
      fn();
    };
    const onMessage = (e: MessageEvent) => {
      if (e.origin !== window.location.origin) return;
      const data = e.data as CallbackMessage;
      if (data?.source !== "google-oauth-callback") return;
      if (data.error) return finish(() => reject(new GoogleOAuthError(data.error!)));
      if (data.state !== expectedState) return finish(() => reject(new GoogleOAuthError("OAuth state mismatch — please retry.")));
      if (!data.code) return finish(() => reject(new GoogleOAuthError("No authorization code was returned.")));
      finish(() => resolve(data.code!));
    };
    const timer = setInterval(() => {
      if (popup.closed) finish(() => reject(new GoogleOAuthError("Sign-in window was closed before finishing.")));
    }, 500);
    window.addEventListener("message", onMessage);
  });
}

// --- capacitor (Android) system-browser + custom-scheme redirect ---

async function runCapacitorFlow(cfg: OAuthConfig, opts: OAuthFlowOptions): Promise<OAuthResult> {
  const [{ Browser }, { App }] = await Promise.all([
    import("@capacitor/browser"),
    import("@capacitor/app"),
  ]);
  const { verifier, challenge } = await createPkcePair();
  const state = randomState();
  const redirectUri = nativeRedirectUri(cfg.clientId);
  const schemePrefix = `${nativeRedirectScheme(cfg.clientId).toLowerCase()}:`;
  const url = buildAuthUrl(cfg, { challenge, state, redirectUri, ...opts });

  const code = await new Promise<string>((resolve, reject) => {
    let settled = false;
    const handles: Array<{ remove: () => Promise<void> }> = [];
    const track = (p: Promise<{ remove: () => Promise<void> }>) =>
      void p.then((h) => (settled ? void h.remove() : handles.push(h)));
    const finish = (fn: () => void) => {
      if (settled) return;
      settled = true;
      for (const h of handles) void h.remove();
      void Browser.close().catch(() => {});
      fn();
    };
    track(
      App.addListener("appUrlOpen", (event: { url: string }) => {
        if (!event.url.toLowerCase().startsWith(schemePrefix)) return; // not our redirect
        const q = event.url.includes("?") ? event.url.slice(event.url.indexOf("?") + 1) : "";
        const params = new URLSearchParams(q);
        const err = params.get("error");
        if (err) return finish(() => reject(new GoogleOAuthError(err)));
        if (params.get("state") !== state) return finish(() => reject(new GoogleOAuthError("OAuth state mismatch — please retry.")));
        const c = params.get("code");
        if (!c) return finish(() => reject(new GoogleOAuthError("No authorization code was returned.")));
        finish(() => resolve(c));
      }),
    );
    // The user dismissed the Custom Tab (or it errored out) without a redirect.
    // Delay so a near-simultaneous successful `appUrlOpen` wins the race; if the
    // redirect had landed, `settled` is already true and this no-ops.
    track(
      Browser.addListener("browserFinished", () => {
        setTimeout(() => finish(() => reject(new GoogleOAuthError("Sign-in was cancelled."))), 600);
      }),
    );
    Browser.open({ url }).catch((e: unknown) =>
      finish(() => reject(new GoogleOAuthError(e instanceof Error ? e.message : String(e)))),
    );
  });
  return { code, verifier, redirectUri };
}
